import {
  EventEmitter,
  getTranslatedText,
  useActivityTracker
} from '@nile/components';
import { GET_AI_RECOMMENDED_TICKET_SUMMARIES, MLClient } from '@nile/graphql';
import { Button } from '@superops/ui-design-system';
import { isEmpty } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
import AIContainerContext from '../../context/AIContainerContext';
import AISimilarTickets from '../AISimilarTickets';
import MonicaLoader from '../MonicaLoader';
import RecommendedSolutionCard from './RecommendedSolutionCard';
import RecommendedWebSearchCard from './RecommendedWebSearchCard';
import { Sheet } from '@shopify/polaris';
import ArticleReader from '@nile/rmm/src/modules/knowledge-base/components/ArticleSearchSheet/ArticleReader';
import { useAIRecommendedSolutionsStore } from '@nile/components/src/Components/AI/store/aiRecommendedSolutionsStore.tsx';

const AIRecommendedSolutions = (props) => {
  const { data, content } = props;

  const aiFeatureHook = AIFeaturesHook();

  const recommendedSolutionMeta = aiFeatureHook?.getAIFeatureData(
    'RECOMMENDED_SOLUTION'
  );

  const getIsWebSearchEnabled = () => {
    const subFeatures = recommendedSolutionMeta?.subFeatures;
    let isEnabled = false;
    if (subFeatures) {
      const webSearchFeature = subFeatures.find(
        (subFeat) => subFeat?.featureId === '8'
      );
      isEnabled = webSearchFeature?.state === 'ACTIVE' || false;
    }
    return isEnabled;
  };

  const isWebSearchEnabled = getIsWebSearchEnabled();

  const showMonicaLoaderOnLoad = isEmpty(content) && isWebSearchEnabled;

  const aiRecommendedSolutions = useAIRecommendedSolutionsStore(
    (s) => s.aiRecommendedSolutions
  );
  const setAIRecommendedSolutions = useAIRecommendedSolutionsStore(
    (s) => s.setAiRecommendedSolutions
  );

  const intl = useIntl();
  const aiContainerContext = useContext(AIContainerContext);

  const parentTicketId = data?.variables?.ticketId;

  const similarTicketLoadingText = [
    getTranslatedText(intl, 'msp.ai.similar.tickets.loading.message.1'),
    getTranslatedText(intl, 'msp.ai.similar.tickets.loading.message.2'),
    getTranslatedText(intl, 'msp.ai.similar.tickets.loading.message.3'),
    getTranslatedText(intl, 'msp.ai.similar.tickets.loading.message.4')
  ];

  const webSearchLoadingText = [
    getTranslatedText(
      intl,
      'ai.recommended.solutions.web.search.loading.message.1'
    ),
    getTranslatedText(
      intl,
      'ai.recommended.solutions.web.search.loading.message.2'
    ),
    getTranslatedText(
      intl,
      'ai.recommended.solutions.web.search.loading.message.3'
    ),
    getTranslatedText(
      intl,
      'ai.recommended.solutions.web.search.loading.message.4'
    )
  ];

  const recommendedTicketHeader = [
    getTranslatedText(intl, 'msp.ai.recommended.tickets.header.message.1'),
    getTranslatedText(intl, 'msp.ai.recommended.tickets.header.message.2'),
    getTranslatedText(intl, 'msp.ai.recommended.tickets.header.message.3'),
    getTranslatedText(intl, 'msp.ai.recommended.tickets.header.message.4')
  ];

  const emptyRecommendedTicketHeader = [
    getTranslatedText(
      intl,
      'msp.ai.recommended.tickets.empty.header.message.1'
    ),
    getTranslatedText(
      intl,
      'msp.ai.recommended.tickets.empty.header.message.2'
    ),
    getTranslatedText(
      intl,
      'msp.ai.recommended.tickets.empty.header.message.3'
    ),
    getTranslatedText(intl, 'msp.ai.recommended.tickets.empty.header.message.4')
  ];

  const showSimilarTickets = useAIRecommendedSolutionsStore(
    (s) => s.showSimilarTickets
  ); // Sets the params for the selected or Clicked AI Feature
  const similarTickets = useAIRecommendedSolutionsStore(
    (s) => s.similarTickets
  ); // Data for the Summary
  const showWebSearch = useAIRecommendedSolutionsStore((s) => s.showWebSearch);

  const setShowSimilarTickets = useAIRecommendedSolutionsStore(
    (s) => s.setShowSimilarTickets
  );
  const setSimilarTickets = useAIRecommendedSolutionsStore(
    (s) => s.setSimilarTickets
  );
  const setShowWebSearch = useAIRecommendedSolutionsStore(
    (s) => s.setShowWebSearch
  );

  const resetRecommendedSolutionsData = useAIRecommendedSolutionsStore(
    (s) => s.resetRecommendedSolutionsData
  );

  const [similarTicketsLoading, setSimilarTicketsLoading] = useState(false);

  const [webSearchLoading, setWebSearchLoading] = useState(
    showMonicaLoaderOnLoad
  );
  const [initalLoad, setInitialLoad] = useState(showMonicaLoaderOnLoad);

  const updateHeaderText = (updatedText) => {
    if (aiContainerContext?.setCustomHeaderText) {
      aiContainerContext.setCustomHeaderText(updatedText || null); // Add lokalise keys
    }
  };

  useEffect(() => {
    if (!aiRecommendedSolutions) {
      setAIRecommendedSolutions(content);
      if (isEmpty(content) && !isWebSearchEnabled) {
        updateHeaderText(emptyRecommendedTicketHeader);
      }
    }
  }, [content]);

  const [getAIRecommendedTickets] = useLazyQuery(
    GET_AI_RECOMMENDED_TICKET_SUMMARIES,
    {
      client: MLClient,
      fetchPolicy: 'no-cache',
      onCompleted: (res) => {
        if (res?.getAIRecommendedTicketSummaries) {
          setSimilarTicketsLoading(false);
          setShowSimilarTickets(true);
          setSimilarTickets(res?.getAIRecommendedTicketSummaries);
          updateHeaderText(
            !isEmpty(res?.getAIRecommendedTicketSummaries)
              ? recommendedTicketHeader
              : emptyRecommendedTicketHeader
          );
        }
      }
    }
  );

  const onRecommendedSolutionDislike = (dislikedData) => {
    const filteredData = aiRecommendedSolutions.filter(
      (el) => el?.title !== dislikedData?.title
    );
    setAIRecommendedSolutions(filteredData);
  };

  const openSimilarTicket = (solution) => {
    const recommendedTickets = solution?.tickets || [];
    const recommendedTicketIds = recommendedTickets.map((el) => el?.ticketId);
    const recommendedTicketVariables = {
      ticketId: parentTicketId,
      recommendedSolution: solution?.summary,
      recommendedSolutionTicketIds: recommendedTicketIds
    };

    getAIRecommendedTickets({
      variables: recommendedTicketVariables
    });
    setSimilarTicketsLoading(true);
    setShowSimilarTickets(true);
    useActivityTracker({
      event: 'Clicked on source tickets from recommended solutions',
      data: {
        ticketId: parentTicketId
      }
    });
  };

  const closeSimilarTicket = () => {
    setShowSimilarTickets(false);
    setSimilarTickets(null);
  };

  const closeWebSearch = () => {
    EventEmitter.dispatch('closeWebSearch');
    setShowWebSearch(false);
    setWebSearchLoading(false);
  };

  const onBack = () => {
    if (showWebSearch) {
      closeWebSearch();
      if (aiContainerContext?.setCustomHeaderText) {
        updateHeaderText(null);
      }
      return;
    }

    if (showSimilarTickets) {
      closeSimilarTicket();
      if (aiContainerContext?.setCustomHeaderText) {
        updateHeaderText(null);
      }
      return;
    }

    EventEmitter.dispatch('globalAIBackClicked');
    resetRecommendedSolutionsData();
    if (aiContainerContext?.onBack) {
      aiContainerContext.onBack();
    }
  };

  const onWebSearchDislike = () => {
    onBack();
  };

  const getLoadingText = () => {
    if (webSearchLoading) {
      return webSearchLoadingText;
    }
    if (similarTicketLoadingText) {
      return similarTicketLoadingText;
    }
    return null;
  };

  const isMonicaLoading = webSearchLoading || similarTicketsLoading;

  const wrapperClasNames = [
    'ai-content-container-max-height br10',
    isMonicaLoading ? 'no-scroll min-height-170' : 'scroll',
    !initalLoad && 'mb-30'
  ]
    .filter(Boolean)
    .join(' ');

  const classNames = [
    'flex col g10 full-height',
    isMonicaLoading
      ? 'no-scroll min-height-170 ai-content-container-max-height br10'
      : 'scroll position-relative'
  ]
    .filter(Boolean)
    .join(' ');

  const [selectedArticle, setSelectedArticle] = useState();
  const [showKb, setShowKb] = useState(false);

  const openArticleSheet = (id) => {
    useActivityTracker({
      event: 'AI_RECOMMENDED_SOLUTION_ARTICLE_READ'
    });
    setSelectedArticle({ id });
    setShowKb(true);
  };

  return (
    <div className={wrapperClasNames} id="wrapper">
      <div className={classNames} id="ai-recommended-solutions-wrapper">
        <MonicaLoader
          loading={webSearchLoading || similarTicketsLoading}
          classNames="full-height"
          loadingText={getLoadingText()}
        />

        {!showSimilarTickets &&
          !showWebSearch &&
          !isEmpty(aiRecommendedSolutions) && (
            <>
              {aiRecommendedSolutions.map((solution, index) => {
                return (
                  <RecommendedSolutionCard
                    data={solution}
                    index={index}
                    parentTicketId={parentTicketId}
                    openSimilarTicket={openSimilarTicket}
                    onDislike={onRecommendedSolutionDislike}
                    openArticleSheet={openArticleSheet}
                  />
                );
              })}
            </>
          )}

        <Sheet open={showKb} onClose={() => setShowKb(false)}>
          <div className="w700 bg-white full-height p2040 flex fcol g20 scroll">
            <ArticleReader
              selectedArticle={selectedArticle}
              closeSheet={() => setShowKb(false)}
            />
          </div>
        </Sheet>

        {showSimilarTickets && (
          <AISimilarTickets
            hideHeader
            content={similarTickets}
            mixpanelEventOnOpen={{
              event: 'Clicked on ticket link from recommended solutions',
              data: {
                ticketId: parentTicketId
              }
            }}
          />
        )}

        {isWebSearchEnabled && (
          <div className={showSimilarTickets && 'hiddenActions'}>
            <RecommendedWebSearchCard
              ticketId={parentTicketId}
              setWebSearchLoading={setWebSearchLoading}
              setShowWebSearch={setShowWebSearch}
              onWebSearchDislike={onWebSearchDislike}
              setInitialLoad={setInitialLoad}
              aiRecommendedSolutions={aiRecommendedSolutions}
            />
          </div>
        )}
      </div>

      {!initalLoad && (
        <div className="position-fixed b0 mb20 ml-5">
          <Button plain onClick={onBack} aiPlain>
            <FormattedMessage id="ticket.newProject.cancel.goback" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default AIRecommendedSolutions;
